/**
 * Robust cleanup for stuck `pointer-events: none` on document.body.
 *
 * Radix UI's react-remove-scroll sets pointer-events: none on body while overlays
 * are open. When cleanup races with React unmount, a single requestAnimationFrame
 * can miss the stuck state. This utility retries at increasing intervals to reliably
 * catch it regardless of timing.
 */

function removeStuckPointerEvents(label: string): boolean {
  if (typeof document === 'undefined') return false;
  const bodyStyle = document.body.style;
  if (bodyStyle.pointerEvents === 'none') {
    bodyStyle.pointerEvents = '';
    bodyStyle.removeProperty('pointer-events');
    if (process.env.NODE_ENV === 'production') {
      console.warn(`[${label}] Removed stuck pointer-events`);
    }
    return true;
  }
  return false;
}

/**
 * Schedule pointer-events cleanup with retries. Call from useEffect cleanup.
 * Checks at ~0ms (rAF), 100ms, and 500ms to cover the race window.
 */
export function schedulePointerEventsCleanup(label: string): void {
  let cleared = false;

  const check = () => {
    if (!cleared) cleared = removeStuckPointerEvents(label);
  };

  requestAnimationFrame(check);
  const t1 = setTimeout(check, 100);
  const t2 = setTimeout(check, 500);

  // Self-cleanup: clear remaining timers once first check succeeds
  // (timers are cheap, but clean code is better)
  setTimeout(() => {
    clearTimeout(t1);
    clearTimeout(t2);
  }, 600);
}
